import Table from '@/components/Table';
import { TableData } from '@/components/Table/type';
import { useNonResearchReasonQuery } from '@/queries/useNonResearchReasonQuery';
import { NonResearchReason } from '@/types/NonResearchReason';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import DadosMestreHeader from '../components/DadosMestreHeader';
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions';
import DefaultColumn from '@/components/Table/components/DefaultColumn';
import { useTable } from '@/hooks/useTable';
import TableRowActions from '@/components/Table/components/TableRowActions';
import AddDialog from './components/AddDialog';
import DeleteDialog from './components/DeleteDialog';
import EditDialog from './components/EditDialog';
import { Badge } from '@/components/ui/badge';
import { FileX } from 'lucide-react';
import { TypographyH1, TypographyMuted } from '@/components/ui/typography';

export type NonResearchReasonTable = TableData<NonResearchReason>;

const columnHelper = createColumnHelper<NonResearchReason>();

const MasterDataNonResearchReason = () => {
  const {
    table,
    tableState,
    selectedRows,
    isAddDialogOpen,
    isDeleteDialogOpen,
    selectedRow,
    isEditDialogOpen,
    onEditDialogClose,
    onAddDialogClose,
    onClickMultiDelete,
    setTable,
    onClickDelete,
    onClickEdit,
    onDeleteDialogClose,
    onClickAdd,
  } = useTable<NonResearchReason>('');

  const { data, isFetching, isPending, refetch } = useNonResearchReasonQuery(false);

  const memoData = useMemo(() => data || [], [data]);

  const memoColumns = useMemo(
    () => [
      columnHelper.accessor('SK_MOTIVO_NAO_PESQUISA', {
        id: 'SK_MOTIVO_NAO_PESQUISA',
        header: 'Código',
        size: 110,
      }),
      columnHelper.accessor('DS_MOTIVO_NAO_PESQUISA', {
        id: 'DS_MOTIVO_NAO_PESQUISA',
        header: 'Descrição do Motivo',
        size: 450,
      }),
      columnHelper.accessor('ATIVO', {
        id: 'ATIVO',
        header: 'Situação',
        size: 150,
        cell: ({ getValue }) => {
          const val = getValue();
          const isAtivo = val === 1 || val === undefined;
          return (
            <Badge variant={isAtivo ? 'default' : 'secondary'}>
              {isAtivo ? 'Ativo' : 'Inativo'}
            </Badge>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => (
          <TableRowActions
            onClickEdit={() => onClickEdit(row)}
            onClickDelete={() => onClickDelete(row)}
          />
        ),
        size: 80,
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [onClickDelete, onClickEdit]
  );

  return (
    <div className="flex flex-col h-full overflow-auto p-4">
      <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
        <div className="flex gap-3 items-center">
          <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
            <FileX className="w-5 h-5 text-primary" />
          </div>
          <div>
            <TypographyH1>Motivos de Não Pesquisa</TypographyH1>
            <TypographyMuted>
              Cadastre e efetue manutenções nos motivos de não realização de pesquisa
            </TypographyMuted>
          </div>
        </div>
      </div>

      <div className="w-full h-full overflow-hidden">
        <Table<NonResearchReasonTable>
          data={memoData}
          columns={memoColumns}
          getRowId={(row) => row.SK_MOTIVO_NAO_PESQUISA.toString()}
          isLoading={isPending}
          isFetching={isFetching}
          getTableInstance={(table) => setTable(table)}
          tableState={tableState}
          tableHeader={
            <DadosMestreHeader
              title={`${(data || []).length} itens`}
              onDelete={onClickMultiDelete}
              selectedRows={
                table
                  ?.getFilteredRowModel()
                  .flatRows.filter((row) => selectedRows[row.id]).length
              }
            />
          }
          tableActions={
            <DadosMestreHeaderActions
              onRefech={refetch}
              onAdd={onClickAdd}
            />
          }
          defaultColumn={{
            cell: ({ getValue }) => (
              <DefaultColumn>{getValue() as string}</DefaultColumn>
            ),
          }}
        />

        <AddDialog
          isOpen={isAddDialogOpen}
          onClose={onAddDialogClose}
        />

        {selectedRow && (
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={onEditDialogClose}
            reason={selectedRow.original}
          />
        )}

        {isDeleteDialogOpen && selectedRow && (
          <DeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={onDeleteDialogClose}
            reason={selectedRow.original}
          />
        )}
      </div>
    </div>
  );
};

export default MasterDataNonResearchReason;
